import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import type { Episode } from "@/types/podcast";

interface EpisodeRowProps {
  episode: Episode;
  isSelected: boolean;
  onToggleSelect: (guid: string) => void;
  onTranscribe: (episode: Episode) => void;
  isTranscribed?: boolean;
  transcriptId?: number;
  isTranscribing?: boolean;
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return "";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const stripHtml = (html: string) =>
  html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&#39;/g, "'").replace(/&quot;/g, '"').trim();

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  // Always show full date: "Feb 18" or "Feb 18, 2025" if different year
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

export default function EpisodeRow({
  episode,
  isSelected,
  onToggleSelect,
  onTranscribe,
  isTranscribed,
  transcriptId,
  isTranscribing,
}: EpisodeRowProps) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-gray-100 bg-white px-4 py-3 transition-colors hover:bg-gray-50">
      <Checkbox
        checked={isSelected}
        onCheckedChange={() => onToggleSelect(episode.guid)}
        className="mt-1"
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-gray-900 line-clamp-1">
            {episode.title}
          </p>
          <div className="flex shrink-0 items-center gap-3 text-xs text-gray-400">
            {episode.duration != null && (
              <span>{formatDuration(episode.duration)}</span>
            )}
            {episode.published_date && (
              <span>{formatDate(episode.published_date)}</span>
            )}
          </div>
        </div>

        {episode.description && (
          <p className="mt-0.5 text-xs text-gray-500">
            {stripHtml(episode.description)}
          </p>
        )}

        <div className="mt-2">
          {isTranscribed && transcriptId ? (
            <Link
              to={`/transcript/${transcriptId}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-100"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="h-3 w-3"
              >
                <path
                  fillRule="evenodd"
                  d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
                  clipRule="evenodd"
                />
              </svg>
              View Transcript
            </Link>
          ) : isTranscribing ? (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-600">
              <Loader2 className="h-3 w-3 animate-spin" />
              Transcribing...
            </span>
          ) : (
            <button
              onClick={() => onTranscribe(episode)}
              className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700"
            >
              Transcribe
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
